import { AiSearchProvider } from "@redotech/redo-aeo-common/ai-search-providers";
import { assertNever } from "@redotech/util/type";

export enum MerchantEmailTrigger {
  DISPUTE_MANUAL_EDIT = "dispute_manual_edit",
  DISPUTE_ONBOARDING_COMPLETE = "dispute_onboarding_complete",
  DISPUTE_ONBOARDING_AUTOMATION_DECLINED = "dispute_onboarding_automation_declined",
  DISPUTE_RECEIVED_NOT_ENROLLED = "dispute_received_not_enrolled",
  DISPUTE_RECEIVED_NOT_ENROLLED_V2 = "dispute_received_not_enrolled_v2",
  DISPUTE_TAB_VIEWED_NO_ACTION = "dispute_tab_viewed_no_action",
  DISPUTE_ALERT_NEEDS_REVIEW = "dispute_alert_needs_review",
  RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED = "reclaim_dispute_won_recover_not_enrolled",
  RECOVER_PAGE_VIEWED_NOT_ENROLLED = "recover_page_viewed_not_enrolled",
  RECOVER_AUTOMATION_VIEWED_NOT_ENROLLED = "recover_automation_viewed_not_enrolled",
  AGENTIC_SHOPPING_ORDER_WITHOUT_AEO = "agentic_shopping_order_without_aeo",
  REFUND_PROCESSED_RECOVER_NOT_ENROLLED = "refund_processed_recover_not_enrolled",
  RECOVER_DAILY_ACTIVITY_SUMMARY = "recover_daily_activity_summary",
  RECOVER_WEEKLY_DIGEST = "recover_weekly_digest",
  CHOP_TEST_COMPLETE_RECOVER_NOT_ENROLLED = "chop_test_complete_recover_not_enrolled",
  RECOVER_CART_RECOVERED = "recover_cart_recovered",
  THEME_PUBLISHED_CHOP_NOT_ENROLLED = "theme_published_chop_not_enrolled",
  THEME_UPDATED_CHOP_NOT_ENROLLED = "theme_updated_chop_not_enrolled",
  ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED = "order_fulfilled_could_have_auto_merged",
  ORDER_WITH_NO_TEXT_CONCIERGE = "order_with_no_text_concierge",
  MANAGED_RETURNS_FIRST_RETURN_PROCESSED = "managed_returns_first_return_processed",
  RETURNS_ONBOARDING_ABANDONED = "returns_onboarding_abandoned",
  REVENUE_MILESTONE_100K = "revenue_milestone_100k",
  REVENUE_MILESTONE_500K = "revenue_milestone_500k",
  REVENUE_MILESTONE_1M = "revenue_milestone_1m",
  REVENUE_MILESTONE_5M = "revenue_milestone_5m",
  REVENUE_MILESTONE_10M = "revenue_milestone_10m",
  REVENUE_MILESTONE_50M = "revenue_milestone_50m",
  REVENUE_MILESTONE_100M = "revenue_milestone_100m",
  SHOPIFY_PLAN_UPGRADED = "shopify_plan_upgraded",
}

export const RECOVER_PROJECTED_RECOVERY_RATE = 0.02;
export const CHOP_PROJECTED_REVENUE_RATE = 0.03;

export enum MerchantEmailRecipientType {
  TEAM_OWNER = "team_owner",
  NOTIFICATION_EMAIL = "notification_email",
  ALL_TEAM_ADMINS = "all_team_admins",
}

export enum MerchantEmailFrequency {
  EVERY_TIME = "every_time",
  COOLDOWN = "cooldown",
  ONCE_PER_TEAM = "once_per_team",
}

export enum MerchantEmailCampaignStatus {
  NEW = "new",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

function revenueMilestoneMeta(milestone: string): {
  label: string;
  description: string;
} {
  return {
    label: `Revenue Milestone — ${milestone}`,
    description: `Merchant's lifetime store sales crossed ${milestone}. Fired by the daily revenue-milestone cron; when several thresholds are passed at once only the highest fires, and pre-existing revenue is seeded silently. Campaigns MUST use Once Per Team.`,
  };
}

export function getMerchantEmailTriggerMeta(trigger: MerchantEmailTrigger): {
  label: string;
  description: string;
} {
  switch (trigger) {
    case MerchantEmailTrigger.DISPUTE_MANUAL_EDIT:
      return {
        label: "Dispute Manual Edit",
        description:
          "Merchant uploaded or edited dispute evidence in Shopify instead of through Redo.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_COMPLETE:
      return {
        label: "Dispute Onboarding Complete",
        description:
          "Merchant completed dispute onboarding with automation enabled. Encourage full integration.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_AUTOMATION_DECLINED:
      return {
        label: "Dispute Onboarding Automation Declined",
        description:
          "Merchant enabled disputes but chose not to enable AI automation.",
      };
    case MerchantEmailTrigger.DISPUTE_RECEIVED_NOT_ENROLLED:
      return {
        label: "Dispute Received Not Enrolled",
        description:
          "A new dispute was received for a merchant who has not enrolled in Redo's dispute product.",
      };
    case MerchantEmailTrigger.DISPUTE_RECEIVED_NOT_ENROLLED_V2:
      return {
        label: "Dispute Received Not Enrolled (V2)",
        description:
          "A new dispute was received for a merchant who has not enrolled in Redo's dispute product. Second campaign on the same signal, allowing a fresh email with independent send history.",
      };
    case MerchantEmailTrigger.DISPUTE_TAB_VIEWED_NO_ACTION:
      return {
        label: "Dispute Tab Viewed No Action",
        description:
          "Merchant visited the disputes tab but navigated away without interacting.",
      };
    case MerchantEmailTrigger.DISPUTE_ALERT_NEEDS_REVIEW:
      return {
        label: "Dispute Alert Needs Review",
        description:
          "An actionable pre-dispute alert (CDRN/ETHOCA) could not be auto-resolved or auto-declined and is waiting on the merchant to act before its response window closes. Configure with a short COOLDOWN so an alert burst produces one nudge.",
      };
    case MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED:
      return {
        label: "Reclaim Dispute Won — Recover Not Enrolled",
        description:
          "A dispute was won for a team using Reclaim that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_PAGE_VIEWED_NOT_ENROLLED:
      return {
        label: "Recover Page Viewed Not Enrolled",
        description:
          "Merchant visited the Recover welcome page but has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_AUTOMATION_VIEWED_NOT_ENROLLED:
      return {
        label: "Recover Automation Viewed Not Enrolled",
        description:
          "Merchant viewed the marketing automations page but has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO:
      return {
        label: "Agentic Shopping Order Without AEO",
        description:
          "Merchant received an order referred from an agentic shopping channel (ChatGPT, Perplexity, Gemini) and is not enrolled in AEO.",
      };
    case MerchantEmailTrigger.REFUND_PROCESSED_RECOVER_NOT_ENROLLED:
      return {
        label: "Refund Processed — Recover Not Enrolled",
        description:
          "A refund was processed for a return on a team that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY:
      return {
        label: "Recover Daily Activity Summary",
        description:
          "Daily summary of Recover messaging activity for teams with active Recover automations.",
      };
    case MerchantEmailTrigger.RECOVER_WEEKLY_DIGEST:
      return {
        label: "Recover Weekly Digest",
        description:
          "Weekly performance digest for active Recover merchants: 7-day recovered revenue, week-over-week change, trailing 4-week revenue, and supporting tiles (orders, conversion rate, AOV). Skips teams with zero recovered revenue in the last 7 days.",
      };
    case MerchantEmailTrigger.CHOP_TEST_COMPLETE_RECOVER_NOT_ENROLLED:
      return {
        label: "CHOP Test Complete — Recover Not Enrolled",
        description:
          "A checkout optimization A/B test was concluded for a team that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_CART_RECOVERED:
      return {
        label: "Recover Cart Recovered",
        description:
          "An abandoned cart was successfully recovered through the Recover product — an order was attributed to a Recover conversation.",
      };
    case MerchantEmailTrigger.THEME_PUBLISHED_CHOP_NOT_ENROLLED:
      return {
        label: "Theme Published — Chop Not Enrolled",
        description:
          "A theme was published for a merchant that has not enrolled in the Chop product.",
      };
    case MerchantEmailTrigger.THEME_UPDATED_CHOP_NOT_ENROLLED:
      return {
        label: "Theme Updated — Chop Not Enrolled",
        description:
          "A theme was updated for a merchant that has not enrolled in the Chop product.",
      };
    case MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED:
      return {
        label: "Order Fulfilled — Could Have Auto-Merged",
        description:
          "An order was fulfilled on a team that is not enrolled in OMS, and Snowflake shows another recent order from the same customer/address that Redo OMS auto-merge could have combined it with.",
      };
    case MerchantEmailTrigger.ORDER_WITH_NO_TEXT_CONCIERGE:
      return {
        label: "Order With No Text Concierge",
        description:
          "An order was delivered for a team that has Support enabled but has not enabled the shopper agent.",
      };
    case MerchantEmailTrigger.MANAGED_RETURNS_FIRST_RETURN_PROCESSED:
      return {
        label: "Managed Returns — First Return Processed",
        description:
          "The first warehouse return was processed for an RMR-enabled team.",
      };
    case MerchantEmailTrigger.RETURNS_ONBOARDING_ABANDONED:
      return {
        label: "Returns Onboarding Abandoned",
        description:
          "A self-serve Returns merchant left onboarding incomplete. Configure with a seven-day cooldown; an inactive campaign disables email delivery.",
      };
    case MerchantEmailTrigger.REVENUE_MILESTONE_100K:
      return revenueMilestoneMeta("$100K");
    case MerchantEmailTrigger.REVENUE_MILESTONE_500K:
      return revenueMilestoneMeta("$500K");
    case MerchantEmailTrigger.REVENUE_MILESTONE_1M:
      return revenueMilestoneMeta("$1M");
    case MerchantEmailTrigger.REVENUE_MILESTONE_5M:
      return revenueMilestoneMeta("$5M");
    case MerchantEmailTrigger.REVENUE_MILESTONE_10M:
      return revenueMilestoneMeta("$10M");
    case MerchantEmailTrigger.REVENUE_MILESTONE_50M:
      return revenueMilestoneMeta("$50M");
    case MerchantEmailTrigger.REVENUE_MILESTONE_100M:
      return revenueMilestoneMeta("$100M");
    case MerchantEmailTrigger.SHOPIFY_PLAN_UPGRADED:
      return {
        label: "Shopify Plan Upgraded",
        description:
          "Merchant's Shopify plan moved up the ladder (e.g. Basic → Grow, Advanced → Plus). Fires only on a genuine upgrade transition; use a short COOLDOWN (24h) as a safety net so separate upgrades months apart still send.",
      };
    default:
      assertNever(trigger);
  }
}

export interface RevenueMilestoneTriggerData {
  /** Lifetime store sales at the time the milestone was crossed, in shop currency. */
  lifetimeRevenue: number;
  /** The threshold that was crossed, in whole shop-currency units (e.g. 1000000). */
  milestone: number;
}

/**
 * Maps triggers to their required triggerData shape.
 * Triggers not listed here require no extra data.
 */
export interface MerchantEmailTriggerDataMap {
  [MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED]: {
    disputeAmount: number;
  };
  [MerchantEmailTrigger.DISPUTE_ALERT_NEEDS_REVIEW]: {
    /** The amount the cardholder is disputing. */
    disputeAmount: number;
    /** ISO 4217 currency of `disputeAmount`, so the email renders the right symbol. */
    disputeCurrency: string;
    /** Alert network: "CDRN" | "ETHOCA". */
    network: string;
    /** RFC 3339 action deadline, or null. */
    respondBy: string | null;
  };
  [MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO]: {
    channel: AiSearchProvider;
  };
  [MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY]: {
    messagesSent: number;
    recoveredRevenue: number;
    recoveredOrders: number;
  };
  [MerchantEmailTrigger.RECOVER_WEEKLY_DIGEST]: {
    /** Recovered revenue over the last 7 days. */
    thisWeekRevenue: number;
    /** Recovered orders over the last 7 days. */
    thisWeekOrders: number;
    /** Initial sends over the last 7 days (denominator for conversion rate, matches the analytics page). */
    thisWeekInitialSends: number;
    /** Recovered revenue over the prior 7-day window (8-14 days ago). */
    priorWeekRevenue: number;
    /** Recovered revenue over the trailing 28 days. */
    trailing4WeeksRevenue: number;
    /**
     * Pre-formatted editorial sentence based on the WoW ladder
     * (e.g. "Up 23% vs last week.", "Best week in the last 4.", "Steady week — 12 orders recovered.").
     * Empty when both this-week and prior-week revenue are below $100.
     */
    highlightLine: string;
    /**
     * Subject-line suffix appended after the recovered-revenue figure (e.g. " (↑ 23%)").
     * Empty when WoW commentary is suppressed.
     */
    subjectSuffix: string;
  };
  [MerchantEmailTrigger.RECOVER_CART_RECOVERED]: {
    recoveredOrderValue: number;
  };
  [MerchantEmailTrigger.MANAGED_RETURNS_FIRST_RETURN_PROCESSED]: {
    returnId: string;
  };
  [MerchantEmailTrigger.RETURNS_ONBOARDING_ABANDONED]: { resumeUrl: string };
  [MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED]: {
    /** Count of fulfilled orders in the last 30 days that had a mergeable sibling. */
    mergeableOrdersLast30Days: number;
    /** Estimated label-cost savings (USD) from avoided duplicate shipments over the last 30 days. */
    potentialSavingsLast30DaysUsd: number;
    /** Most recent fulfilled order with a mergeable sibling. */
    mostRecentOrderName: string;
    mostRecentOrderUrl: string;
    /** The sibling order the most recent one would have been merged with. */
    mostRecentSiblingOrderName: string;
    mostRecentSiblingOrderUrl: string;
  };
  [MerchantEmailTrigger.REVENUE_MILESTONE_100K]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_500K]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_1M]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_5M]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_10M]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_50M]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.REVENUE_MILESTONE_100M]: RevenueMilestoneTriggerData;
  [MerchantEmailTrigger.SHOPIFY_PLAN_UPGRADED]: {
    /** Display name of the plan before the upgrade (e.g. "Advanced"). */
    previousPlan: string;
    /** Display name of the plan after the upgrade (e.g. "Shopify Plus"). */
    newPlan: string;
    isPlusUpgrade: boolean;
  };
}

type TriggersWithData = keyof MerchantEmailTriggerDataMap;
type TriggersWithoutData = Exclude<MerchantEmailTrigger, TriggersWithData>;

export type MerchantEmailTriggerInput =
  | {
      [K in TriggersWithData]: {
        trigger: K;
        triggerData: MerchantEmailTriggerDataMap[K];
      };
    }[TriggersWithData]
  | { trigger: TriggersWithoutData };

export interface MerchantEmailVariableMeta {
  name: string;
  description: string;
  example: string;
}

/** Always populated from team data. */
export const MERCHANT_EMAIL_BASE_VARIABLES: readonly MerchantEmailVariableMeta[] =
  [
    {
      name: "teamId",
      description: "The team's unique identifier",
      example: "507f1f77bcf86cd799439011",
    },
    {
      name: "teamName",
      description: "The team's display name",
      example: "Acme Store",
    },
  ];

/** Resolved at fire time via Shopify API. */
export const MERCHANT_EMAIL_COMPUTED_VARIABLES: readonly MerchantEmailVariableMeta[] =
  [
    {
      name: "monthlyOrders",
      description: "Number of orders in the last 30 days",
      example: "4,200",
    },
    {
      name: "averageOrderValue",
      description: "Average order value in the last 30 days",
      example: "$85.00",
    },
    {
      name: "monthlyProjectedRecoveredRevenue",
      description:
        "Estimated monthly recovered revenue (orders x AOV x 2% recovery rate)",
      example: "$7,140",
    },
    {
      name: "monthlyProjectedChopRevenue",
      description: "Estimated monthly Chop revenue (orders x AOV x 3%)",
      example: "$10,710",
    },
  ];

const REVENUE_MILESTONE_VARIABLES: readonly MerchantEmailVariableMeta[] = [
  {
    name: "lifetimeRevenue",
    description: "Lifetime store sales at the time the milestone was crossed",
    example: "$1,042,381",
  },
  {
    name: "revenueMilestone",
    description: "The milestone threshold that was crossed",
    example: "$1M",
  },
];

/**
 * Trigger-specific variables, keyed by trigger.
 * Each entry's triggerData field (from MerchantEmailTriggerDataMap)
 * produces the template variables listed here.
 */
export const MERCHANT_EMAIL_TRIGGER_VARIABLES: Partial<
  Record<MerchantEmailTrigger, readonly MerchantEmailVariableMeta[]>
> = {
  // triggerData.disputeAmount → {{disputeAmountWon}}
  [MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED]: [
    {
      name: "disputeAmountWon",
      description: "Amount of the most recently won dispute",
      example: "$150",
    },
  ],
  // triggerData → {{disputeAlertAmount}}, {{disputeAlertNetwork}}, {{disputeAlertRespondBy}}
  [MerchantEmailTrigger.DISPUTE_ALERT_NEEDS_REVIEW]: [
    {
      name: "disputeAlertAmount",
      description: "Amount the cardholder is disputing on the alert",
      example: "$49.99",
    },
    {
      name: "disputeAlertNetwork",
      description: "Alert network (CDRN or ETHOCA)",
      example: "ETHOCA",
    },
    {
      name: "disputeAlertRespondBy",
      description: "Deadline to act before the alert escalates to a chargeback",
      example: "Jun 14, 2026",
    },
  ],
  // triggerData.channel → {{agenticChannel}}
  [MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO]: [
    {
      name: "agenticChannel",
      description:
        "Friendly name of the agentic shopping channel that referred the order",
      example: "ChatGPT",
    },
  ],
  // triggerData.messagesSent → {{recoverMessagesSentToday}}, etc.
  [MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY]: [
    {
      name: "recoverMessagesSentToday",
      description: "Number of Recover messages sent today",
      example: "42",
    },
    {
      name: "recoverRevenueToday",
      description: "Revenue recovered through Recover today",
      example: "$3,850",
    },
    {
      name: "recoverOrdersToday",
      description: "Number of orders recovered through Recover today",
      example: "12",
    },
  ],
  [MerchantEmailTrigger.RECOVER_WEEKLY_DIGEST]: [
    {
      name: "recoverRevenueThisWeek",
      description: "Recovered revenue over the last 7 days",
      example: "$4,823.50",
    },
    {
      name: "recoverRevenueTrailing4Weeks",
      description: "Recovered revenue over the trailing 28 days",
      example: "$18,402.00",
    },
    {
      name: "recoverOrdersThisWeek",
      description: "Recovered orders over the last 7 days",
      example: "12",
    },
    {
      name: "recoverConversionRateThisWeek",
      description:
        "Conversion rate over the last 7 days (recoveredOrders / initialSends), matches the analytics dashboard",
      example: "8.4%",
    },
    {
      name: "recoverAvgOrderValueThisWeek",
      description:
        "Average recovered order value over the last 7 days (recoveredRevenue / recoveredOrders), matches the analytics dashboard",
      example: "$402.00",
    },
    {
      name: "recoverHighlightLine",
      description:
        "Editorial sentence based on the WoW ladder (best week / up / steady / down / dipped). Empty when both this-week and prior-week revenue are below $100.",
      example: "Up 23% vs last week.",
    },
    {
      name: "recoverSubjectSuffix",
      description:
        "Suffix to append to the subject line after the recovered-revenue figure. Empty when WoW is suppressed.",
      example: " (↑ 23%)",
    },
  ],
  // triggerData.recoveredOrderValue → {{recoveredOrderValue}}
  [MerchantEmailTrigger.RECOVER_CART_RECOVERED]: [
    {
      name: "recoveredOrderValue",
      description: "Value of the recovered order",
      example: "$127",
    },
  ],
  [MerchantEmailTrigger.MANAGED_RETURNS_FIRST_RETURN_PROCESSED]: [
    {
      name: "returnId",
      description: "The ID of the first processed return",
      example: "6a4586c34310a28534d2ae70",
    },
    {
      name: "returnUrl",
      description: "Direct link to the processed return",
      example:
        "https://app.getredo.com/stores/507f1f77bcf86cd799439011/returns/returns/all-returns/6a4586c34310a28534d2ae70",
    },
    {
      name: "dashboardUrl",
      description: "Link to the managed returns dashboard",
      example:
        "https://app.getredo.com/stores/507f1f77bcf86cd799439011/returns/analytics/managed-returns",
    },
    {
      name: "flaggedUrl",
      description: "Link to flagged returns",
      example:
        "https://app.getredo.com/stores/507f1f77bcf86cd799439011/returns/returns/flagged",
    },
    {
      name: "tasksUrl",
      description: "Link to merchant tasks",
      example:
        "https://app.getredo.com/stores/507f1f77bcf86cd799439011/returns/managed-returns/tasks",
    },
  ],
  [MerchantEmailTrigger.RETURNS_ONBOARDING_ABANDONED]: [
    {
      name: "resumeUrl",
      description: "Authenticated merchant-app path back to Returns onboarding",
      example: "https://app.getredo.com/onboarding-redirect?src=resume-email",
    },
  ],
  [MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED]: [
    {
      name: "mergeableOrdersLast30Days",
      description:
        "Number of fulfilled orders in the last 30 days that Redo OMS auto-merge would have combined with another shipment",
      example: "14",
    },
    {
      name: "potentialSavingsLast30Days",
      description:
        "Estimated label-cost savings over the last 30 days from those avoided duplicate shipments",
      example: "$84",
    },
    {
      name: "mostRecentMergeableOrder",
      description:
        "The merchant-facing name of the most recent fulfilled order that could have been auto-merged (e.g. #1001)",
      example: "#1001",
    },
    {
      name: "mostRecentMergeableOrderUrl",
      description:
        "A link to that most recent order in the merchant's storefront admin",
      example: "https://acme-store.myshopify.com/admin/orders/1234567890",
    },
    {
      name: "mostRecentMergeableSibling",
      description:
        "The merchant-facing name of the sibling order the most recent one would have been merged with",
      example: "#1002",
    },
    {
      name: "mostRecentMergeableSiblingUrl",
      description:
        "A link to the sibling order in the merchant's storefront admin",
      example: "https://acme-store.myshopify.com/admin/orders/1234567891",
    },
  ],
  [MerchantEmailTrigger.REVENUE_MILESTONE_100K]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_500K]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_1M]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_5M]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_10M]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_50M]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.REVENUE_MILESTONE_100M]: REVENUE_MILESTONE_VARIABLES,
  [MerchantEmailTrigger.SHOPIFY_PLAN_UPGRADED]: [
    {
      name: "previousShopifyPlan",
      description: "The merchant's Shopify plan before the upgrade",
      example: "Advanced",
    },
    {
      name: "newShopifyPlan",
      description: "The merchant's Shopify plan after the upgrade",
      example: "Shopify Plus",
    },
  ],
};

// Deduped by name — the milestone triggers share one variable set.
export const MERCHANT_EMAIL_VARIABLES: readonly MerchantEmailVariableMeta[] = [
  ...new Map(
    [
      ...MERCHANT_EMAIL_BASE_VARIABLES,
      ...MERCHANT_EMAIL_COMPUTED_VARIABLES,
      ...Object.values(MERCHANT_EMAIL_TRIGGER_VARIABLES).flat(),
    ].map((variable) => [variable.name, variable] as const),
  ).values(),
];

export function extractMerchantEmailVariables(template: string): string[] {
  const names = new Set<string>();
  const known = new Set(MERCHANT_EMAIL_VARIABLES.map((v) => v.name));
  for (const match of template.matchAll(/\{\{(\w+)\}\}/g)) {
    const name = match[1];
    if (name !== undefined && known.has(name)) {
      names.add(name);
    }
  }
  return [...names];
}

export function interpolateMerchantEmailTemplate(
  template: string,
  variables: Record<string, string>,
): string {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (match, key: string) => variables[key] ?? match,
  );
}

export function getMerchantEmailCampaignStatusMeta(
  status: MerchantEmailCampaignStatus,
): { label: string; badgeColor: "gray" | "success" | "warning" } {
  switch (status) {
    case MerchantEmailCampaignStatus.NEW:
      return { label: "New", badgeColor: "gray" };
    case MerchantEmailCampaignStatus.ACTIVE:
      return { label: "Active", badgeColor: "success" };
    case MerchantEmailCampaignStatus.ARCHIVED:
      return { label: "Archived", badgeColor: "warning" };
    default:
      assertNever(status);
  }
}
